// Dedicated esbuild entry for the /learn "stage that breathes" page (Task #239).
// -------------------------------------------------------------------------
// Like the /tutor and /courses/<id>/lesson entries, this is INTENTIONALLY
// separate from `all.js` (the /lab + home bundle). `all.js` pulls in main.js's
// Lab boot AND expose-globals.js (which initializes a hardcoded Firebase
// project), so this standalone surface must NOT use it. This entry imports ONLY
// the resident-legend 3D stack + the TutorStageController — no Firebase init.
//
// ModelViewer + GreetingService + the rest of the 3D/audio pipeline are pulled
// in transitively by ResidentLegend. `three` stays an external bare specifier
// (served from the version-pinned importmap, same as /lab, /tutor, /lesson).
//
// esbuild bundles this to static/dist/stage-<hash>.js (see scripts/build.mjs).

import * as characterDataNS from "../src/js/modules/characterData.js";
import { ResidentLegend } from "../src/js/modules/ResidentLegend.js";
import tutorStage from "../src/js/modules/TutorStageController.js";
import {
  installCaptureControls,
  isCaptureRequested,
  resolveCaptureTarget,
} from "../src/js/modules/LessonCapture.js";

// Expose the usable records object (with getCharacterById), not the namespace.
window.characterData = characterDataNS.characterData || characterDataNS;
window.ResidentLegend = ResidentLegend;
window.tutorStage = tutorStage;

function renderTopicPrompt(cfg, onSubmit) {
  const who = cfg.characterName || "Your mentor";
  const wrap = document.createElement("div");
  wrap.className = "stage-prompt";
  wrap.innerHTML =
    '<div class="stage-prompt-card">' +
    '<p class="stage-prompt-eyebrow"></p>' +
    '<h1 class="stage-prompt-title">What should I teach you?</h1>' +
    '<form class="stage-prompt-form">' +
    '<input class="stage-prompt-input" type="text" autocomplete="off" ' +
    'placeholder="e.g. how compound interest works" aria-label="Lesson topic" />' +
    '<button class="stage-prompt-go" type="submit">Begin the lesson \u2192</button>' +
    "</form>" +
    '<p class="stage-prompt-hint"></p>' +
    "</div>";
  // textContent (not innerHTML) for the name so it can never inject markup.
  wrap.querySelector(".stage-prompt-eyebrow").textContent = who + " is ready";
  wrap.querySelector(".stage-prompt-hint").textContent =
    who + " will give you a short, spoken lecture.";
  document.body.appendChild(wrap);

  const form = wrap.querySelector(".stage-prompt-form");
  const input = wrap.querySelector(".stage-prompt-input");
  setTimeout(() => { try { input.focus(); } catch (_) {} }, 60);
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const topic = (input.value || "").trim();
    if (!topic) { try { input.focus(); } catch (_) {} return; }
    // This submit IS the user gesture — unlock + bless narration audio here so
    // the topic-prompt path is audible on Safari too (its lesson start happens
    // after an await, no longer in a gesture).
    unlockAudioOnGesture();
    wrap.classList.add("is-leaving");
    setTimeout(() => { try { wrap.remove(); } catch (_) {} }, 360);
    onSubmit(topic);
  });
}

// Task #492 — boot breadcrumb helper. The recorder itself is a classic inline
// script in tutor_stage.html (exists before this bundle); this is a safe
// pass-through so every milestone in the begin chain is observable.
function bootMark(name, detail) {
  try {
    if (window.__llBoot && typeof window.__llBoot.mark === "function") {
      window.__llBoot.mark(name, detail);
    }
  } catch (_) {}
}

// Task #492 — QA-only tuning knobs for the boot watchdogs (mirrors ?preview=1
// in spirit: never used by real visitors). The boot-floor check harness sets
// window.__llBootTuning to shrink the ceilings so stall shapes prove out in
// seconds instead of minutes.
function bootTuned(key, dflt) {
  try {
    const t = window.__llBootTuning;
    const v = t && t[key];
    if (typeof v === "number" && isFinite(v) && v > 0) return v;
  } catch (_) {}
  return dflt;
}

// Task #492 — the model warm-up is a known 24–64s; the gate ceiling only
// exists so a HUNG load (stalled GLB fetch, wedged WebGL init, headless
// browser with no GL at all) can never strand the Begin gate forever. It
// must comfortably exceed the slow-but-healthy case.
const MODEL_GATE_CEILING_MS = 90000;
// Task #492 — the authored-lesson JSON fetch is a small same-origin GET; if it
// hasn't answered in this window something is wedged. One silent retry, then
// the error card (a visible dead end beats an invisible one).
const LESSON_FETCH_TIMEOUT_MS = 20000;

// Task #492 — fetch-and-parse with an AbortController deadline so no await in
// the begin chain is unbounded. The deadline covers the WHOLE exchange —
// headers AND body — because a stalled response stream strands exactly like
// stalled headers (res.json() rejects with AbortError on expiry).
async function fetchJsonWithTimeout(url, opts, ms) {
  const ac = typeof AbortController !== "undefined" ? new AbortController() : null;
  const merged = Object.assign({}, opts || {}, ac ? { signal: ac.signal } : {});
  let timer = null;
  if (ac) timer = setTimeout(() => { try { ac.abort(); } catch (_) {} }, ms);
  try {
    const res = await fetch(url, merged);
    return await res.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Task #492 — never-strand model gate: wait for the load to settle OR the
// ceiling, whichever comes first. The underlying load is NEVER aborted — if it
// completes after a timeout the modelLive hard-cut still fires and the teacher
// pops in mid-lesson. Returns "live" | "failed" | "timeout".
//
// IMPORTANT: callers go through the memoized awaitModelGate() built in boot(),
// NEVER by calling this per await site — each call arms its own ceiling timer,
// so two sequential awaits would stack into 2× the ceiling (the exact
// unbounded-ish wait this gate exists to kill).
function modelGate(loadP) {
  const ceiling = bootTuned("modelGateMs", MODEL_GATE_CEILING_MS);
  return Promise.race([
    loadP.then((ok) => (ok ? "live" : "failed")),
    new Promise((r) => setTimeout(() => r("timeout"), ceiling)),
  ]).then((state) => {
    if (state === "timeout") {
      console.error("[stage] model gate ceiling (" + ceiling + "ms) — starting without the 3D teacher");
      bootMark("model-gate-timeout", { ceilingMs: ceiling });
      try { if (window.__llWarmup && window.__llWarmup.modelSlow) window.__llWarmup.modelSlow(); } catch (_) {}
    }
    return state;
  });
}

// A tiny, universally-decodable, truly-silent WAV (8 kHz / 8-bit / mono).
// Playing a REAL source inside a user gesture is what actually grants the
// document "media engagement"; an empty `new Audio()` rejects with
// NotSupportedError and unlocks NOTHING — which is why Safari/iOS and the
// embedded preview iframe stayed silent even though audio decoded fine for A2F.
const SILENT_AUDIO_URI =
  "data:audio/wav;base64,UklGRjQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YRAAAACAgICAgICAgICAgICAgICA";

// Task #251/#254 — bless audio within a real user gesture. The global
// `__audioGestureUnlocked` already flips on the first pointerdown (a
// capture-once listener registered by GreetingService), and the TTS pipeline
// calls audioCtx.resume() at play time. This belt-and-suspenders ALSO nudges
// any already-created (suspended) audio context AND — critically — plays a real
// silent clip so the document gains media engagement. That makes the later
// async <audio>.play() (the greeting + every beat, which runs AFTER a fetch and
// the A2F lead-in, no longer in the gesture call stack) actually audible on
// desktop Chrome, Safari/iOS, and inside the preview iframe. It NEVER pauses or
// reconfigures the sacred pipeline.
function unlockAudioOnGesture() {
  try { window.__audioGestureUnlocked = true; } catch (_) {}
  try {
    const rtts = window.realtimeTTS || window.tts || null;
    if (rtts && rtts.audioCtx && typeof rtts.audioCtx.resume === "function") {
      rtts.audioCtx.resume().catch(() => {});
    }
  } catch (_) {}
  try {
    const a = new Audio(SILENT_AUDIO_URI);
    a.preload = "auto";
    const p = a.play();
    if (p && typeof p.then === "function") {
      p.then(() => { try { a.pause(); a.currentTime = 0; } catch (_) {} }).catch(() => {});
    }
  } catch (_) {}
  // Safari/iOS unlock audio PER-ELEMENT, so the silent clip above (a throwaway
  // element) grants NOTHING to the greeting's own `new Audio()`. Bless the
  // lesson's PERSISTENT narration element inside this same gesture so every
  // beat that plays after a fetch (out of the gesture stack) stays audible.
  try {
    if (window.tutorStage && typeof window.tutorStage.blessNarrationAudio === "function") {
      window.tutorStage.blessNarrationAudio();
    }
  } catch (_) {}
}

// ── Task #515 — auto-start with graceful unlock (the Begin card is GONE) ────
// The founder's ruling: opening a lesson should just start it. Browsers void
// gesture-granted audio across a navigation, so the course-page click can't
// authorize sound here — instead:
//   auto           — when the stage is ready, ATTEMPT playback (a real, silent
//                    clip on the persistent narration element). Some browsers
//                    allow it; then the lesson simply begins.
//   early-gesture  — ANY tap/click/keypress while the stage is preparing is
//                    treated as the unlock gesture (silent-WAV bless contract),
//                    and the lesson begins the moment it's ready.
//   tap            — only when the browser REFUSED playback and no gesture ever
//                    arrived: one minimal, on-theme "Tap to begin" affordance
//                    on the stage itself (a beat in the flow, not a page).
// Every path is breadcrumbed (start-auto / start-early-gesture / start-tap) and
// every await stays bounded (boot-floor law).

let __started = false;       // a lesson start has been committed
let __gestureSeen = false;   // a REAL user activation happened on this page
let __armedBegin = null;     // set while the tap affordance waits for a gesture
let __tapEl = null;
let __noteEl = null;

// Any activation-worthy interaction during the preparing phase doubles as the
// audio unlock. With navigator.userActivation we read the truth; without it
// (old Safari) only end-of-interaction events count — a touch-scroll's
// pointerdown must never masquerade as a gesture (it grants nothing, and a
// false flag here would mean a silently blocked start).
const EARLY_UNLOCK_EVENTS = ["pointerdown", "pointerup", "touchend", "keydown", "click"];
let __earlyUnlockOn = false;
function onEarlyGesture(e) {
  if (__started) return;
  unlockAudioOnGesture();
  let active = true;
  try {
    const ua = navigator.userActivation;
    active = ua
      ? !!(ua.hasBeenActive || ua.isActive)
      : (e.type === "pointerup" || e.type === "touchend" || e.type === "keydown" || e.type === "click");
  } catch (_) {}
  if (!active) return;
  if (!__gestureSeen) {
    __gestureSeen = true;
    bootMark("early-gesture", { type: e.type });
  }
  if (__armedBegin) { const f = __armedBegin; __armedBegin = null; f(); }
}
function installEarlyUnlock() {
  if (__earlyUnlockOn) return;
  __earlyUnlockOn = true;
  EARLY_UNLOCK_EVENTS.forEach((t) => {
    try { window.addEventListener(t, onEarlyGesture, { capture: true, passive: true }); } catch (_) {}
  });
}
// MUST be removed the moment a start commits: unlockAudioOnGesture re-blesses
// the persistent narration element (swaps its src), which would cut off a
// PLAYING line if it kept firing on every mid-lesson click/keystroke.
function removeEarlyUnlock() {
  if (!__earlyUnlockOn) return;
  __earlyUnlockOn = false;
  EARLY_UNLOCK_EVENTS.forEach((t) => {
    try { window.removeEventListener(t, onEarlyGesture, { capture: true }); } catch (_) {}
  });
}

// The auto-start probe: attempt the SAME real-silent-clip play the gesture
// bless performs, on the SAME persistent narration element — with no gesture.
// Resolved ⇒ the browser allows audible autoplay here AND the element is now
// blessed. Rejected ⇒ a gesture is still required. Bounded (boot-floor law):
// a play() promise that never settles counts as blocked, and the visible tap
// affordance is the recovery.
function probeNarrationAutoplay() {
  const ceiling = bootTuned("autoplayProbeMs", 4000);
  let attempt = null;
  try {
    if (window.tutorStage && typeof window.tutorStage.blessNarrationAudio === "function") {
      attempt = window.tutorStage.blessNarrationAudio();
    }
  } catch (_) {}
  if (!attempt || typeof attempt.then !== "function") {
    attempt = new Promise((res) => {
      try {
        const a = new Audio(SILENT_AUDIO_URI);
        a.preload = "auto";
        const p = a.play();
        if (p && typeof p.then === "function") {
          p.then(() => { try { a.pause(); } catch (_) {} res(true); }, () => res(false));
        } else res(true);
      } catch (_) { res(false); }
    });
  }
  return Promise.race([
    Promise.resolve(attempt).then((ok) => ok !== false, () => false),
    new Promise((r) => setTimeout(() => r(false), ceiling)),
  ]);
}

// ONE committed start per page, whatever path won the race. Tears down the
// pre-start chrome (tap pill, status note, early-unlock listeners) and names
// the path in the boot breadcrumbs so a stalled start names its own gate.
function commitStart(path, begin) {
  if (__started) return;
  __started = true;
  removeEarlyUnlock();
  hideTapStart();
  clearStageNote();
  bootMark("start-" + path);
  try { begin(); } catch (e) { console.error("[stage] begin failed", e); }
}

// The minimal on-theme fallback — shown ONLY when playback was refused and no
// gesture ever arrived. It sits on the stage (backdrop + teacher stay visible).
// Deliberately NOT on the demo hide list: like the Begin button it replaces,
// it IS the start control when a capture browser blocks autoplay, and it
// leaves the stage before any footage-worthy frame.
function showTapStart(onTap) {
  if (__tapEl || __started) return;
  const b = document.createElement("button");
  b.type = "button";
  b.className = "stage-tapstart";
  b.setAttribute("aria-label", "Tap to begin the lesson");
  b.innerHTML = '<span class="stage-tapstart-dot"></span><span class="stage-tapstart-label"></span>';
  b.querySelector(".stage-tapstart-label").textContent = "Tap to begin";
  b.addEventListener("click", () => {
    try { onTap(); } catch (e) { console.error("[stage] tap begin failed", e); }
  });
  document.body.appendChild(b);
  __tapEl = b;
}
function hideTapStart() {
  const el = __tapEl;
  __tapEl = null;
  if (!el) return;
  el.classList.add("is-leaving");
  setTimeout(() => { try { el.remove(); } catch (_) {} }, 320);
}

// Same pill, non-interactive: the post-checkout "Activating your Premium…"
// status while the webhook lands (the old Begin card used to carry this text).
function ensureStageNote(text) {
  if (!__noteEl) {
    const d = document.createElement("div");
    d.className = "stage-tapstart is-note";
    d.innerHTML = '<span class="stage-tapstart-spin"></span><span class="stage-tapstart-label"></span>';
    document.body.appendChild(d);
    __noteEl = d;
  }
  try { __noteEl.querySelector(".stage-tapstart-label").textContent = text; } catch (_) {}
}
function clearStageNote() {
  const el = __noteEl;
  __noteEl = null;
  if (el) { try { el.remove(); } catch (_) {} }
}

// The start decision, run once the stage is ready (lesson resolved + model
// gate settled). Exactly one of the three paths commits the start.
async function autoStartOrUnlock(begin) {
  if (__started) return;
  if (__gestureSeen) { commitStart("early-gesture", begin); return; }
  const ok = await probeNarrationAutoplay();
  bootMark("autoplay-probe", { ok: !!ok });
  if (__started) return;
  if (ok || __gestureSeen) {
    commitStart(__gestureSeen ? "early-gesture" : "auto", begin);
    return;
  }
  // Refused, zero interaction so far: arm BOTH the visible affordance and the
  // page-wide gesture listeners — whichever tap comes first begins the lesson.
  bootMark("unlock-shown");
  showTapStart(() => { unlockAudioOnGesture(); commitStart("tap", begin); });
  __armedBegin = () => commitStart("tap", begin);
}

function boot() {
  bootMark("bundle-boot");
  let cfg = {};
  try {
    const el = document.getElementById("stage-config");
    if (el) cfg = JSON.parse(el.textContent || "{}") || {};
  } catch (e) {
    console.error("[stage] bad stage-config JSON", e);
  }
  bootMark("config-parsed", {
    lessonRef: !!(cfg.lessonRef && cfg.lessonRef.courseId),
    topic: !!cfg.topic,
  });
  const captureRequested = isCaptureRequested(cfg, window.location.search) &&
    !!(cfg.lessonRef && cfg.lessonRef.courseId && cfg.lessonRef.lessonId);
  let captureActive = false;

  const canvas = document.getElementById("legend-canvas");
  const resident = new ResidentLegend({
    canvas,
    // Every lesson teacher uses the Lab's Studio look, without reading or
    // overwriting the Lab's independently saved Classic/Studio preference.
    lightingMode: "studio",
    // Only the catalog lesson player uses the posed-silhouette, eye-level
    // composition. Other resident surfaces and the Lab keep their own camera.
    framingPolicy: "lesson",
    fullBodyFit: { heightMargin: 1.08, widthMargin: 1.10 },
  });
  window.residentLegend = resident;

  // Kick the heavy 3D model load off IMMEDIATELY — it IS the load cover while
  // the learner reads the prompt / the lesson script is generated. load() is
  // idempotent so awaiting it again later just returns the same promise.
  bootMark("model-load-start");
  const loadP = resident.load(cfg.characterId).then(
    () => {
      try { resident.show(); } catch (_) {}
      // A late avatar must not overwrite a capture verdict/reaction that has
      // already happened while the model was downloading.
      if (!captureActive) {
        try { resident.setState("idle"); } catch (_) {}
      }
      // Task #399 — the teacher is LIVE: hard-cut the warming portrait so the
      // real model owns the corner this frame (no dissolve, per the loader
      // ruling). Chrome-only; never touches the 3D pipeline.
      try { if (window.__llWarmup) window.__llWarmup.modelLive(); } catch (_) {}
      bootMark("model-live");
      return true;
    },
    (e) => {
      console.error("[stage] resident load failed", e);
      // Keep the warming portrait (a face beats an empty corner) but stop
      // promising a model that isn't coming.
      try { if (window.__llWarmup) window.__llWarmup.modelFailed(); } catch (_) {}
      bootMark("model-failed", { error: String((e && e.message) || e || "unknown").slice(0, 200) });
      return false;
    },
  );
  // Task #492 — ONE memoized gate for the whole page: the ceiling timer arms
  // at the FIRST await site and every later await (other branch, the paywall
  // justSubscribed retry loop, …) reuses the same settled promise instead of
  // stacking another full ceiling on top.
  let modelGateP = null;
  const awaitModelGate = () => {
    if (!modelGateP) modelGateP = modelGate(loadP);
    return modelGateP;
  };

  const residentRecord = () => {
    try {
      return resident.getCharacterRecord
        ? resident.getCharacterRecord(cfg.characterId)
        : null;
    } catch (_) {
      return null;
    }
  };

  // `gated` = a user gesture already happened (the topic-prompt submit), so we
  // can start immediately. The prebaked cfg.topic path has no gesture of its
  // own — it auto-starts (or shows the minimal unlock) exactly like the
  // authored-lesson path (Task #515).
  const startLecture = async (topic, lopts) => {
    const opts = lopts || {};
    const begin = () => {
      // Task #399 — start → first words is a silent fetch+gate window; show
      // the "gathering the first words" pill until ll:first-words fires.
      try { if (window.__llWarmup) window.__llWarmup.awaitVoice(); } catch (_) {}
      tutorStage.start({
        standalone: true,
        resident,
        record: residentRecord(),
        characterId: cfg.characterId,
        characterName: cfg.characterName,
        characterSlug: cfg.characterSlug,
        resultLearnerId: cfg.resultLearnerId,
        topic,
      });
    };
    // Task #492 — bounded: a hung model load can no longer strand the start.
    await awaitModelGate();
    if (opts.gated) {
      commitStart("topic-submit", begin);
      return;
    }
    bootMark("stage-ready");
    autoStartOrUnlock(begin);
  };

  // Task #246 — a preauthored Brilliant lesson: fetch authored beats (gating
  // checked server-side via the same-origin session) and hand them to the same
  // standalone stage instead of generating from a topic. Task #515: no Begin
  // card — once ready it auto-starts, rides an early gesture, or shows the
  // minimal tap affordance (autoStartOrUnlock).
  const startLesson = async (ref, lopts) => {
    const sopts = lopts || {};

    // Task #492 — the two begin-chain dependencies are awaited SEPARATELY and
    // each is bounded: the lesson fetch carries a deadline + one silent retry,
    // and the model waits behind the never-strand gate ceiling. A model
    // failure/timeout no longer has any way to take the lesson down with it —
    // it degrades (portrait + captions) while the lesson still plays.
    const lessonUrl =
      "/api/learn/course/" + encodeURIComponent(ref.courseId) +
      "/lesson/" + encodeURIComponent(ref.lessonId);
    const fetchLesson = async () => {
      const headers = {};
      try {
        if (window.__lastIdToken) headers.Authorization = "Bearer " + window.__lastIdToken;
      } catch (_) {}
      return fetchJsonWithTimeout(
        lessonUrl,
        { headers, credentials: "same-origin" },
        bootTuned("lessonFetchMs", LESSON_FETCH_TIMEOUT_MS),
      );
    };
    let payload = null;
    bootMark("lesson-fetch-start");
    try {
      payload = await fetchLesson();
    } catch (e) {
      console.error("[stage] lesson fetch failed — retrying once", e);
      bootMark("lesson-fetch-retry", { error: String((e && e.name) || e).slice(0, 80) });
      try {
        payload = await fetchLesson();
      } catch (e2) {
        console.error("[stage] lesson fetch failed twice", e2);
        bootMark("lesson-fetch-failed", { error: String((e2 && e2.name) || e2).slice(0, 80) });
      }
    }
    if (payload) bootMark("lesson-fetched", { locked: !!payload.locked, success: !!payload.success });
    // Capture resolves only against this authorized response (locked/error
    // payloads still follow the normal gate below), and deliberately does so
    // before the independent avatar gate.
    if (captureRequested && payload && !payload.locked &&
        payload.success && payload.lesson) {
      const target = resolveCaptureTarget(payload.lesson, window.location.search);
      if (!target.ok) {
        clearStageNote();
        try { if (window.__llWarmup) window.__llWarmup.hidePill(); } catch (_) {}
        renderLessonError(cfg, target.error);
        return;
      }
      captureActive = true;
      __started = true;
      removeEarlyUnlock();
      hideTapStart();
      clearStageNote();
      bootMark("capture-start", { index: target.index, selector: target.selector });
      tutorStage.start({
        standalone: true,
        resident,
        record: residentRecord(),
        characterId: cfg.characterId,
        characterName: cfg.characterName,
        characterSlug: cfg.characterSlug,
        resultLearnerId: cfg.resultLearnerId,
        courseId: ref.courseId,
        lessonId: ref.lessonId,
        lesson: payload.lesson,
        captureMode: true,
        captureBeat: target.index,
      });
      installCaptureControls(tutorStage);
      return;
    }
    // Capture never bypasses authorization, but a denied/missing authorized
    // payload also must not wait for an unrelated avatar download.
    if (captureRequested && payload && payload.locked) {
      clearStageNote();
      try { if (window.__llWarmup) window.__llWarmup.hidePill(); } catch (_) {}
      renderLessonGate(cfg, payload, ref);
      return;
    }
    if (captureRequested && (!payload || !payload.success || !payload.lesson)) {
      clearStageNote();
      try { if (window.__llWarmup) window.__llWarmup.hidePill(); } catch (_) {}
      renderLessonError(cfg);
      return;
    }
    // The normal start needs the model settled (or its ceiling) — the lesson JSON
    // alone is not a stage. Bounded by the ONE memoized model gate, never by
    // the raw load promise.
    await awaitModelGate();
    if (payload && payload.locked) {
      // Just back from a completed checkout? The Stripe webhook that flips the
      // subscription in Firestore can lag a few seconds, so poll a handful of
      // times before falling back to the paywall instead of bouncing the new
      // subscriber straight back into it. The minimal stage note carries the
      // status the old Begin card used to show.
      if (sopts.justSubscribed && (sopts.tries || 0) < 5) {
        ensureStageNote("Activating your Premium\u2026");
        setTimeout(() => {
          startLesson(ref, { justSubscribed: true, tries: (sopts.tries || 0) + 1, pendingAsk: sopts.pendingAsk, startBeat: sopts.startBeat || 0 });
        }, 2500);
        return;
      }
      clearStageNote();
      // Paywall: the learner has to act, nothing is "warming" (Task #399).
      try { if (window.__llWarmup) window.__llWarmup.hidePill(); } catch (_) {}
      renderLessonGate(cfg, payload, ref);
      return;
    }
    if (!payload || !payload.success || !payload.lesson) {
      clearStageNote();
      try { if (window.__llWarmup) window.__llWarmup.hidePill(); } catch (_) {}
      renderLessonError(cfg);
      return;
    }
    const lesson = payload.lesson;
    clearStageNote();
    bootMark("stage-ready");
    autoStartOrUnlock(() => {
      // Task #399 — fill the start → first-words gap (see startLecture).
      try { if (window.__llWarmup) window.__llWarmup.awaitVoice(); } catch (_) {}
      tutorStage.start({
        standalone: true,
        resident,
        record: residentRecord(),
        characterId: cfg.characterId,
        characterName: cfg.characterName,
        characterSlug: cfg.characterSlug,
        resultLearnerId: cfg.resultLearnerId,
        courseId: ref.courseId,
        lessonId: ref.lessonId,
        lesson,
        pendingAsk: sopts.pendingAsk || "",
        // Demo deep link (&beat=N): fast-forward the stage to beat N —
        // prior beats reveal instantly, then the lesson runs normally.
        startBeat: sopts.startBeat || 0,
      });
    });
  };

  const startLessonPreview = async (ref, beat, chat) => {
    let payload = null;
    try {
      // Task #492 — bounded like the live path, so QA rigs fail visibly too.
      payload = await fetchJsonWithTimeout(
        "/api/learn/course/" + encodeURIComponent(ref.courseId) +
          "/lesson/" + encodeURIComponent(ref.lessonId),
        { credentials: "same-origin" },
        bootTuned("lessonFetchMs", LESSON_FETCH_TIMEOUT_MS),
      );
    } catch (e) {
      console.error("[stage] preview fetch failed", e);
    }
    if (!payload || !payload.lesson) {
      try { if (window.__llWarmup) window.__llWarmup.hidePill(); } catch (_) {}
      renderLessonError(cfg);
      return;
    }
    tutorStage.start({
      standalone: true,
      resident,
      record: residentRecord(),
      characterId: cfg.characterId,
      characterName: cfg.characterName,
      characterSlug: cfg.characterSlug,
      resultLearnerId: cfg.resultLearnerId,
      courseId: ref.courseId,
      lessonId: ref.lessonId,
      lesson: payload.lesson,
      previewMode: true,
      previewBeat: beat || 0,
      previewChat: !!chat,
    });
  };

  // Task #251 — QA preview path. The screenshot/headless tooling has NO WebGL,
  // so the model never loads and the start would wait out the full model-gate
  // ceiling. `?preview=1` skips the start flow entirely, fetches the authored
  // beats, and renders the 2D board directly (speech no-ops without a loaded
  // resident). Optional `&beat=N` jumps straight to a specific beat. Never
  // used by real visitors.
  let __qa = null;
  try { __qa = new URLSearchParams(window.location.search); } catch (_) {}
  // Resolve capture before *any* QA, checkout-return, or pending-ask shortcut.
  // Those flows may replace the URL or introduce a gate; neither belongs to a
  // targeted take. Entitlement is still checked by startLesson's real request.
  if (captureRequested) {
    startLesson(cfg.lessonRef);
    return;
  }
  if (!captureRequested && __qa && __qa.get("preview") === "1" &&
      cfg.lessonRef && cfg.lessonRef.courseId && cfg.lessonRef.lessonId) {
    startLessonPreview(cfg.lessonRef, parseInt(__qa.get("beat") || "0", 10) || 0, __qa.get("chat") === "1");
    return;
  }
  // QA-only renders for the two screens that otherwise hang behind the model
  // load in the no-WebGL headless tooling: `?preview=gate` (paywall) and
  // `?preview=unlock` (post-checkout celebration). Never used by real visitors.
  if (__qa && __qa.get("preview") === "gate") {
    renderLessonGate(cfg, { title: (cfg.lessonRef && cfg.lessonRef.lessonId) ? "Reading fractions" : "This lesson" }, cfg.lessonRef);
    return;
  }
  if (__qa && __qa.get("preview") === "unlock") {
    renderPremiumUnlocked(cfg, () => {});
    return;
  }

  // Returning from Stripe Checkout: ?subscription_success=true (set by the
  // checkout success_url) means a real, completed paid checkout. Strip the
  // param, play the "Premium Unlocked!" celebration, then load the now-unlocked
  // lesson (with a short retry while the webhook lands). ?subscription_cancelled
  // just returns to the lesson, which re-shows the gate. Never trusted for any
  // entitlement — gating is always re-checked server-side on the next fetch.
  let justSubscribed = false;
  try {
    if (__qa && __qa.get("subscription_success") === "true") justSubscribed = true;
    if (__qa && (__qa.get("subscription_success") || __qa.get("subscription_cancelled"))) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  } catch (_) {}

  // Task #264 — the course page's "Ask the teacher" dock sends the learner here
  // with a question (?ask=...). Carry it into the lesson so the resident legend
  // answers it before the lecture begins. Stripped from the URL so a reload /
  // resume doesn't re-ask the same question.
  let pendingAsk = "";
  try {
    if (__qa) pendingAsk = (__qa.get("ask") || "").trim();
    if (pendingAsk) {
      const __u = new URL(window.location.href);
      __u.searchParams.delete("ask");
      window.history.replaceState({}, document.title, __u.pathname + __u.search);
    }
  } catch (_) {}

  // Demo deep link: &beat=N on the real lesson URL (non-preview) starts the
  // full stage fast-forwarded to beat N (0-based, same unit as ?preview&beat).
  // Audio unlock rides the normal start paths (auto / early gesture / tap).
  let startBeat = 0;
  try {
    if (__qa) startBeat = Math.max(0, parseInt(__qa.get("beat") || "0", 10) || 0);
  } catch (_) {}

  // Task #515 — everything below is a REAL visitor flow: from this moment any
  // interaction while the stage prepares doubles as the audio-unlock gesture
  // (the QA/preview branches above returned already and need no audio).
  if (!captureRequested) installEarlyUnlock();

  const proceed = () => {
    if (cfg.lessonRef && cfg.lessonRef.courseId && cfg.lessonRef.lessonId) {
      const sopts = {};
      if (justSubscribed) sopts.justSubscribed = true;
      if (pendingAsk) sopts.pendingAsk = pendingAsk;
      if (startBeat > 0) sopts.startBeat = startBeat;
      startLesson(cfg.lessonRef, sopts);
    } else if (cfg.topic) {
      startLecture(cfg.topic);
    } else {
      // The topic prompt's submit IS the gesture → commitStart("topic-submit").
      renderTopicPrompt(cfg, (topic) => startLecture(topic, { gated: true }));
    }
  };

  if (justSubscribed) {
    renderPremiumUnlocked(cfg, proceed);
    return;
  }
  proceed();
}

// Display-only pricing — the real charge always comes from the Stripe price
// referenced by STRIPE_MONTHLY_PRICE_ID / STRIPE_YEARLY_PRICE_ID. Kept in step
// with BackendSubscriptionChecker.PRICING (the /lab source of truth).
const PAYGATE_PRICING = {
  yearly: { amount: "$39.99", cadence: "/yr", note: "\u2248 $3.33 / mo \u00b7 billed yearly", badge: "Save 78%" },
  monthly: { amount: "$14.99", cadence: "/mo", note: "Billed monthly \u00b7 cancel anytime" },
};

// Brilliant-style premium checkout gate shown when a lesson is locked for this
// user. Plan picker (yearly default) \u2192 Stripe Checkout. Identity is the same
// same-origin Flask session that gated the lesson; the server decides the real
// price and ownership (we only send a validated priceId + an internal returnTo).
function renderLessonGate(cfg, payload, ref) {
  const who = cfg.characterName || "your guide";
  const lessonTitle = (payload && payload.title) ? payload.title : "This lesson";
  const wrap = document.createElement("div");
  wrap.className = "stage-gate stage-paygate";

  const plan = (key) => {
    const p = PAYGATE_PRICING[key];
    return (
      '<button type="button" class="stage-plan' + (key === "yearly" ? " is-active" : "") +
      '" data-plan="' + key + '">' +
      (p.badge ? '<span class="stage-plan-badge">' + p.badge + "</span>" : "") +
      '<span class="stage-plan-name">' + (key === "yearly" ? "Yearly" : "Monthly") + "</span>" +
      '<span class="stage-plan-amt">' + p.amount + "<small>" + p.cadence + "</small></span>" +
      '<span class="stage-plan-note">' + p.note + "</span>" +
      "</button>"
    );
  };

  wrap.innerHTML =
    '<div class="stage-paygate-card">' +
    '<span class="stage-paygate-crown">\u265b</span>' +
    '<p class="stage-paygate-eyebrow">Premium lesson</p>' +
    '<h1 class="stage-paygate-title"></h1>' +
    '<p class="stage-paygate-sub"></p>' +
    '<ul class="stage-paygate-feats">' +
    "<li>Every lesson, skill-check & review</li>" +
    "<li>All legends and learning paths</li>" +
    "<li>Unlimited practice and XP</li>" +
    "</ul>" +
    '<div class="stage-paygate-plans">' + plan("yearly") + plan("monthly") + "</div>" +
    '<button type="button" class="stage-paygate-go">Unlock Premium \u2192</button>' +
    '<p class="stage-paygate-err" role="alert" hidden></p>' +
    '<a class="stage-paygate-back" href="/learn/catalog">Maybe later</a>' +
    "</div>";

  wrap.querySelector(".stage-paygate-title").textContent =
    "\u201c" + lessonTitle + "\u201d is part of Premium";
  wrap.querySelector(".stage-paygate-sub").textContent =
    "The first lesson of every path is free. Go Premium to keep learning with " + who + ".";

  document.body.appendChild(wrap);

  // Plan selection.
  let chosen = "yearly";
  const plans = wrap.querySelectorAll(".stage-plan");
  plans.forEach((btn) => {
    btn.addEventListener("click", () => {
      chosen = btn.getAttribute("data-plan") || "yearly";
      plans.forEach((b) => b.classList.toggle("is-active", b === btn));
      try { window.lessonSound && window.lessonSound.play("choice"); } catch (_) {}
    });
  });

  const err = wrap.querySelector(".stage-paygate-err");
  const showErr = (msg) => { err.textContent = msg; err.hidden = false; };

  // CTA \u2192 Stripe Checkout.
  const go = wrap.querySelector(".stage-paygate-go");
  go.addEventListener("click", async () => {
    err.hidden = true;
    const original = go.textContent;
    go.disabled = true;
    go.textContent = "Redirecting\u2026";
    try {
      const idsRes = await fetch("/api/price-ids", { credentials: "same-origin" });
      const ids = await idsRes.json().catch(() => ({}));
      const priceId = ids && ids[chosen];
      if (!priceId) throw new Error("That plan isn\u2019t available right now.");

      const returnTo = (function () {
        try { return window.location.pathname; } catch (_) { return "/learn/catalog"; }
      })();
      const res = await fetch("/create-checkout-session", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priceId, returnTo }),
      });
      if (res.status === 401) {
        showErr("Please sign in first, then unlock Premium.");
        go.disabled = false; go.textContent = original;
        return;
      }
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || "Checkout couldn\u2019t start (" + res.status + ").");
      }
      const data = await res.json();
      if (!data.checkoutUrl) throw new Error("No checkout URL received.");
      window.location.href = data.checkoutUrl;
    } catch (e) {
      console.error("[stage] checkout error", e);
      showErr((e && e.message) || "Could not start checkout. Please try again.");
      go.disabled = false; go.textContent = original;
    }
  });
}

// "Premium Unlocked!" celebration shown after returning from a completed Stripe
// Checkout (?subscription_success=true). Plays a short fanfare, then hands off
// to `onContinue` (which loads the now-unlocked lesson).
function renderPremiumUnlocked(cfg, onContinue) {
  const wrap = document.createElement("div");
  wrap.className = "stage-gate stage-unlock";
  let confetti = "";
  for (let i = 0; i < 14; i++) confetti += '<i class="stage-confetti-bit"></i>';
  wrap.innerHTML =
    '<div class="stage-unlock-card">' +
    '<div class="stage-confetti">' + confetti + "</div>" +
    '<div class="stage-unlock-badge">\u2713</div>' +
    '<p class="stage-unlock-eyebrow">Premium unlocked</p>' +
    '<h1 class="stage-unlock-title">You\u2019re all in!</h1>' +
    '<p class="stage-unlock-sub">Every lesson, skill-check, and legend is yours now. Let\u2019s pick up right where you left off.</p>' +
    '<button type="button" class="stage-unlock-go">Start learning \u2192</button>' +
    "</div>";
  document.body.appendChild(wrap);

  try {
    if (window.lessonSound) {
      window.lessonSound.unlock();
      window.lessonSound.sequence(["leaguesUnlocked", "courseComplete"], 360);
    }
  } catch (_) {}

  const finish = () => {
    wrap.classList.add("is-leaving");
    setTimeout(() => { try { wrap.remove(); } catch (_) {} }, 360);
    try { if (typeof onContinue === "function") onContinue(); } catch (_) {}
  };
  wrap.querySelector(".stage-unlock-go").addEventListener("click", finish, { once: true });
}

// A simple full-screen card shown when authored beats can't be loaded.
function renderLessonError(cfg, detail) {
  const wrap = document.createElement("div");
  wrap.className = "stage-prompt";
  wrap.innerHTML =
    '<div class="stage-prompt-card">' +
    '<h1 class="stage-prompt-title">This lesson slipped away</h1>' +
    '<p class="stage-prompt-hint"></p>' +
    '<form class="stage-prompt-form" style="justify-content:center">' +
    '<a class="stage-prompt-go" href="/learn/catalog" style="text-decoration:none">Back to paths \u2192</a>' +
    "</form>" +
    "</div>";
  document.body.appendChild(wrap);
  wrap.querySelector(".stage-prompt-hint").textContent =
    detail || "We couldn\u2019t load it just now. Please try again in a moment.";
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
